 import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PropertyCard from '../components/PropertyCard.js';
import PropertyModal from '../components/PropertyModal.js';
import Layout from '../layout/Layout.js';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
import { loadCSS } from '../utils/cssLoader';

export default function Home() {

    const [activeTab, setActiveTab] = useState('destacadas'); // inicial
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [fade, setFade] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    // Filtros
    const [filtroModalidad, setFiltroModalidad] = useState("");
    const [filtroTipo, setFiltroTipo] = useState("");
    const [filtroUbicacion, setFiltroUbicacion] = useState("");
    // Captura el valor del nombre de propiedad buscado
    const [filtroNombre, setFiltroNombre] = useState("");
    const [resultadoFiltrado, setResultadoFiltrado] = useState(null);
    // Estado para mostrar mensaje de filtro aplicado
    const [filtroAplicadoMensaje, setFiltroAplicadoMensaje] = useState("");
    // Estado para paginación
    const [currentPage, setCurrentPage] = useState(1);
    const propertiesPerPage = 12; // 4 columnas x 3 filas = 12 propiedades por página

    console.log("Component rendered - Properties length:", properties.length, "Is loading:", isLoading, "Filtered results:", resultadoFiltrado ? resultadoFiltrado.length : 'null');

    // Load CSS files for Properties page
    useEffect(() => {
        loadCSS('Properties.css');
        loadCSS('PropertyCard.css');
        loadCSS('PropertyModal.css');
    }, []);

    // Debug: Log when filtered results change
    useEffect(() => {
        console.log("🔄 resultadoFiltrado state changed:", resultadoFiltrado ? resultadoFiltrado.length : 'null', "at", new Date().toLocaleTimeString());
    }, [resultadoFiltrado]);

    const propiedadesFiltradas = activeTab === 'destacadas'
        ? properties.filter(p => p.destacado === true)
        : properties.filter(
            p => JSON.parse(p.caracteristicas || '{}').modalidad === 'remate'
        );

    const clearAllFilters = () => {
        setFiltroNombre("");
        setFiltroModalidad("");
        setFiltroTipo("");
        setFiltroUbicacion("");
        setFiltroAplicadoMensaje("");
        localStorage.removeItem('ubicacion');
    };

    // Funciones de paginación
    const resetPagination = () => {
        setCurrentPage(1);
    };

    const getPaginatedProperties = (propertiesArray) => {
        const startIndex = (currentPage - 1) * propertiesPerPage;
        const endIndex = startIndex + propertiesPerPage;
        return propertiesArray.slice(startIndex, endIndex);
    };

    const getTotalPages = (totalProperties) => {
        return Math.ceil(totalProperties / propertiesPerPage);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTabChange = (tab) => {
        setFade(false); // inicia fade out
        setTimeout(() => {
            setActiveTab(tab);
            resetPagination(); // Reset pagination when changing tabs
            setFade(true); // inicia fade in
        }, 200); // duración del fade out en ms
    };

    useEffect(() => {
        setIsLoading(true);
        console.log("🔄 Iniciando carga de propiedades...");
        const apiUrl = 'https://inmobiliariaorsan.com.mx/api/propiedad';
        console.log("API URL:", apiUrl);
        console.log("🌐 Fetching properties from:", apiUrl);
        fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Origin': 'https://inmobiliariaorsan.com.mx'
            },
            cache: 'no-store',
            credentials: 'same-origin'
        })
        .then(async res => {
            console.log("📡 Response status:", res.status);
            console.log("📡 Response ok:", res.ok);
            console.log("📄 Response headers:", Object.fromEntries(res.headers.entries()));
            
            if (!res.ok) {
                const text = await res.text();
                console.error("❌ Error response content:", text);
                throw new Error(`HTTP error! status: ${res.status}, content: ${text.substring(0, 200)}...`);
            }
            
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await res.text();
                console.error("❌ Unexpected content type:", contentType);
                console.error("❌ Response content:", text);
                throw new Error(`Expected JSON but got ${contentType || 'unknown type'}`);
            }
            
            return res.json();
        })
        .then(data => {
            console.log("✅ Properties received:", data);
            if (data && data.data && Array.isArray(data.data)) {
                console.log("✅ Setting properties from data.data array");
                setProperties(data.data);
            } else if (Array.isArray(data)) {
                console.log("✅ Setting properties from direct array");
                setProperties(data);
            } else {
                console.error('❌ Invalid data format:', data);
                setProperties([]);
            }
            setIsLoading(false);
            console.log("✅ Loading completed, isLoading set to false");
            
            // Aplicar filtro automático desde localStorage después de cargar las propiedades
            const ubicacionGuardada = localStorage.getItem('ubicacion');
                if (ubicacionGuardada) {
                    console.log("🎯 Aplicando filtro automático de ubicación:", ubicacionGuardada);
                    setFiltroUbicacion(ubicacionGuardada);
                    setFiltroAplicadoMensaje(`Mostrando propiedades en ${ubicacionGuardada}`);
                    // Limpiar el localStorage después de aplicar el filtro
                    localStorage.removeItem('ubicacion');
                    // Ocultar el mensaje después de 5 segundos
                    setTimeout(() => {
                        setFiltroAplicadoMensaje("");
                    }, 5000);
                }
            })
            .catch(err => {
                console.error("❌ Error fetching properties:", err);
                console.error("❌ Error message:", err.message);
                console.error("❌ Full error:", err);
                setProperties([]);
                setIsLoading(false);
                console.log("❌ Loading completed with error, isLoading set to false");
            });
    }, []);

    useEffect(() => {
        console.log('Filtros cambiados:', { filtroModalidad, filtroTipo, filtroUbicacion, filtroNombre });
        
        if (filtroModalidad || filtroTipo || filtroUbicacion || filtroNombre) {
            const resultados = properties.filter(p => {
                const c = JSON.parse(p.caracteristicas || '{}');
                const modalidad = (c.modalidad || '').toLowerCase();
                const tipo = (c.inmueble || '').toLowerCase();
                const ubicacion = (c.ubicacion || '').toLowerCase();
                const titulo = (p.titulo || '').toLowerCase();
                
                const matchModalidad = (!filtroModalidad || modalidad === filtroModalidad);
                const matchTipo = (!filtroTipo || tipo === filtroTipo);
                const matchUbicacion = (!filtroUbicacion || ubicacion.includes(filtroUbicacion.toLowerCase()));
                const matchNombre = (!filtroNombre || titulo.includes(filtroNombre.toLowerCase()));
                
                return matchModalidad && matchTipo && matchUbicacion && matchNombre;
            });
            
            console.log(`Filtros aplicados - Resultados: ${resultados.length} de ${properties.length} propiedades`);
            setResultadoFiltrado(resultados);
        } else {
            console.log('Sin filtros activos - ocultando resultados de búsqueda');
            setResultadoFiltrado(null);
        }
    }, [properties, filtroModalidad, filtroNombre, filtroTipo, filtroUbicacion]);

    return (
        <Layout >
            <Helmet>
                <title>Propiedades en Venta y Renta Xalapa 2025 | Casas Departamentos Terrenos | Orsan Inmobiliaria</title>
                <meta name="description" content={`🏠 ${properties.length} Propiedades en Venta y Renta en Xalapa, Veracruz. Casas desde $500,000 MXN, Departamentos, Terrenos, Locales. Remates Inmobiliarios. ✓ Precios Actualizados ✓ Ubicaciones Premium ✓ Asesoría Personalizada`} />
                <meta name="keywords" content="casas en venta xalapa 2025, departamentos en renta xalapa, terrenos xalapa, casas coatepec, propiedades banderilla, remates inmobiliarios veracruz, casas puebla, locales comerciales xalapa, propiedades destacadas, venta casas xalapa, renta departamentos veracruz, terrenos coatepec, casas emiliano zapata, propiedades orsan inmobiliaria" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Orsan Consorcio Inmobiliario" />
                <meta name="language" content="es-MX" />
                <meta name="geo.region" content="MX-VER" />
                <meta name="geo.placename" content="Xalapa" />
                <meta name="geo.position" content="19.5438;-96.9102" />
                <meta name="ICBM" content="19.5438, -96.9102" />
                <meta name="revisit-after" content="1 days" />
                <meta name="distribution" content="global" />
                <meta name="rating" content="general" />
                <meta name="classification" content="Real Estate, Property Sales, Property Rentals" />
                <meta name="category" content="Real Estate" />
                <meta name="coverage" content="Xalapa, Veracruz, Mexico" />
                <meta name="target" content="casas venta xalapa, departamentos renta veracruz, terrenos coatepec" />
                <meta name="HandheldFriendly" content="True" />
                <meta name="MobileOptimized" content="width" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                <meta name="theme-color" content="#00921E" />
                <meta name="msapplication-TileColor" content="#00921E" />
                <meta name="msapplication-TileImage" content="/logo-orsan.webp" />
                <meta property="og:title" content={`🏠 ${properties.length} Propiedades en Venta y Renta - Xalapa 2025 | Orsan`} />
                <meta property="og:description" content={`Descubre las mejores propiedades en Xalapa, Veracruz. Casas, departamentos, terrenos y locales. ✓ Remates Inmobiliarios ✓ Precios Competitivos ✓ Asesoría Profesional`} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={window.location.origin + "/xalapa.webp"} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="Propiedades en Xalapa - Orsan Inmobiliaria" />
                <meta property="og:site_name" content="Orsan Inmobiliaria" />
                <meta property="og:locale" content="es_MX" />
                <meta property="article:author" content="Orsan Consorcio Inmobiliario" />
                <meta property="article:publisher" content="https://www.facebook.com/orsaninmobiliaria" />
                <meta property="article:section" content="Real Estate" />
                <meta property="article:tag" content="Propiedades Xalapa" />
                <meta property="article:tag" content="Casas en Venta" />
                <meta property="article:tag" content="Departamentos en Renta" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`🏠 ${properties.length} Propiedades Xalapa 2025 | Orsan Inmobiliaria`} />
                <meta name="twitter:description" content="Casas, departamentos, terrenos y locales en venta y renta en Xalapa, Veracruz. Remates inmobiliarios disponibles." />
                <meta name="twitter:image" content={window.location.origin + "/xalapa.webp"} />
                <meta name="twitter:image:alt" content="Propiedades en Xalapa - Vista panorámica" />
                <meta name="twitter:site" content="@orsaninmobiliaria" />
                <meta name="twitter:creator" content="@orsaninmobiliaria" />
                <link rel="canonical" href={window.location.href} />
                <link rel="alternate" hreflang="es-mx" href={window.location.href} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Orsan Consorcio Inmobiliario",
                        "alternateName": "Orsan Inmobiliaria",
                        "url": window.location.origin,
                        "logo": window.location.origin + "/logo-orsan.webp",
                        "description": "Inmobiliaria especializada en venta y renta de propiedades en Xalapa, Veracruz y región. Más de 15 años de experiencia en el sector inmobiliario.",
                        "foundingDate": "2008",
                        "founders": [
                            {
                                "@type": "Person",
                                "name": "Equipo Orsan"
                            }
                        ],
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Centro Histórico",
                            "addressLocality": "Xalapa",
                            "addressRegion": "Veracruz",
                            "postalCode": "91000",
                            "addressCountry": "MX"
                        },
                        "contactPoint": [
                            {
                                "@type": "ContactPoint",
                                "telephone": "+52-228-366-7786",
                                "contactType": "customer service",
                                "availableLanguage": "Spanish",
                                "contactOption": "TollFree"
                            },
                            {
                                "@type": "ContactPoint",
                                "url": "mailto:ventas_orsan@hotmail.com",
                                "email": "ventas_orsan@hotmail.com",
                                "contactType": "customer service"
                            }
                        ],
                        "sameAs": [
                            "https://www.facebook.com/orsaninmobiliaria",
                            "https://www.instagram.com/orsaninmobiliaria"
                        ],
                        "hasOfferCatalog": {
                            "@type": "OfferCatalog",
                            "name": "Servicios Inmobiliarios",
                            "itemListElement": [
                                {
                                    "@type": "Offer",
                                    "itemOffered": {
                                        "@type": "Service",
                                        "name": "Venta de Propiedades",
                                        "description": "Venta de casas, departamentos, terrenos y locales comerciales en Xalapa y región"
                                    }
                                },
                                {
                                    "@type": "Offer",
                                    "itemOffered": {
                                        "@type": "Service",
                                        "name": "Renta de Propiedades",
                                        "description": "Renta de inmuebles residenciales y comerciales"
                                    }
                                },
                                {
                                    "@type": "Offer",
                                    "itemOffered": {
                                        "@type": "Service",
                                        "name": "Remates Inmobiliarios",
                                        "description": "Remates judiciales y voluntarios de propiedades"
                                    }
                                }
                            ]
                        },
                        "areaServed": [
                            {
                                "@type": "City",
                                "name": "Xalapa",
                                "addressCountry": "MX"
                            },
                            {
                                "@type": "City",
                                "name": "Coatepec",
                                "addressCountry": "MX"
                            },
                            {
                                "@type": "City",
                                "name": "Banderilla",
                                "addressCountry": "MX"
                            },
                            {
                                "@type": "City",
                                "name": "Veracruz",
                                "addressCountry": "MX"
                            },
                            {
                                "@type": "City",
                                "name": "Puebla",
                                "addressCountry": "MX"
                            }
                        ],
                        "priceRange": "$$",
                        "paymentAccepted": "Cash, Credit Card, Bank Transfer",
                        "currenciesAccepted": "MXN"
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Propiedades en Venta y Renta - Xalapa y Región",
                        "description": `Descubre ${properties.length} propiedades disponibles en Xalapa, Coatepec, Banderilla y región. Casas, departamentos, terrenos y locales en venta y renta.`,
                        "url": window.location.href,
                        "inLanguage": "es-MX",
                        "isPartOf": {
                            "@type": "WebSite",
                            "name": "Orsan Inmobiliaria",
                            "url": window.location.origin
                        },
                        "about": {
                            "@type": "Organization",
                            "name": "Orsan Consorcio Inmobiliario"
                        },
                        "mainEntity": {
                            "@type": "ItemList",
                            "name": "Propiedades Disponibles",
                            "numberOfItems": properties.length,
                            "itemListElement": properties.slice(0, 20).map((property, index) => ({
                                "@type": "ListItem",
                                "position": index + 1,
                                "item": {
                                    "@type": "RealEstateListing",
                                    "name": property.titulo,
                                    "description": property.descripcion,
                                    "url": `${window.location.origin}/propiedad/${property.id_propiedad}`,
                                    "address": {
                                        "@type": "PostalAddress",
                                        "addressLocality": JSON.parse(property.caracteristicas || '{}').ubicacion || "Xalapa",
                                        "addressRegion": "Veracruz",
                                        "addressCountry": "MX"
                                    },
                                    "price": property.precio,
                                    "priceCurrency": "MXN",
                                    "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                                    "availability": "https://schema.org/InStock",
                                    "image": Array.isArray(property.fotos) && property.fotos.length > 0 ? property.fotos[0] : `${window.location.origin}/logo-orsan.webp`
                                }
                            }))
                        },
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Inicio",
                                    "item": window.location.origin
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "Propiedades",
                                    "item": window.location.href
                                }
                            ]
                        }
                    })}
                </script>
            </Helmet>
            <Header active="propiedades" />
            <main style={{ margin: "0px 30px 30px 30px", fontFamily: 'Inclusive Sans', flex: 1 }}>
                <section className="properties-hero-section">
                    <div className="hero-content" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        textAlign: 'center',
                        width: '100%',
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '40px 20px'
                    }}>
                        <h1 className="hero-title" style={{ textAlign: 'center', marginBottom: '20px' }}>
                            Encuentra tu <span style={{ color: "white" }}>Próxima Propiedad</span> con Nosotros.
                        </h1>
                        <p className="hero-subtitle" style={{ textAlign: 'center', marginBottom: '40px' }}>
                            Compra y renta de inmuebles  <span style={{ color: "white" }}>en Xalapa y la región.</span>
                        </p>
                        <div className="search-form" style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: '15px', 
                            margin: '0 auto', 
                            maxWidth: '900px',
                            padding: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '15px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                        }}>
                            <input
                                type="text"
                                placeholder="Buscar propiedad por nombre"
                                value={filtroNombre}
                                onChange={(e) => setFiltroNombre(e.target.value)}
                                className="search-input"
                            />
                            <select
                                className="search-select"
                                value={filtroModalidad}
                                onChange={(e) => setFiltroModalidad(e.target.value)}
                            >
                                <option value="">Modalidad</option>
                                <option value="venta">Venta</option>
                                <option value="renta">Renta</option>
                                <option value="remate">Remate</option>
                            </select>
                            <select
                                className="search-select"
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value.toLowerCase())}
                            >
                                <option value="">Tipo</option>
                                <option value="casa">Casa</option>
                                <option value="departamento">Departamento</option>
                                <option value="terreno">Terreno</option>
                                <option value="local">Local</option>
                            </select>
                            <select
                                className="search-select"
                                value={filtroUbicacion}
                                onChange={(e) => setFiltroUbicacion(e.target.value)}
                            >
                                {
                                    filtroUbicacion &&
                                    !["Xalapa", "Coatepec", "Emiliano Zapata", "Banderilla", "Veracruz", "Puebla"].includes(filtroUbicacion) && (
                                        <option value={filtroUbicacion}>{filtroUbicacion}</option>
                                    )
                                }
                                <option value="">Ubicación</option>
                                <option value="Xalapa">Xalapa</option>
                                <option value="Coatepec">Coatepec</option>
                                <option value="Emiliano Zapata">Emiliano Zapata</option>
                                <option value="Banderilla">Banderilla</option>
                                <option value="Veracruz">Veracruz</option>
                                <option value="Puebla">Puebla</option>
                            </select>
                            <button
                                className="search-button"
                                onClick={clearAllFilters}
                            >
                                Limpiar Filtros
                            </button>
                        </div>
                    </div>

                </section>

                {/* Mensaje de filtro aplicado automáticamente */}
                {filtroAplicadoMensaje && (
                    <div style={{
                        backgroundColor: '#e8f5e8',
                        border: '2px solid #4caf50',
                        borderRadius: '8px',
                        padding: '15px 20px',
                        margin: '20px 30px',
                        textAlign: 'center',
                        color: '#2e7d32',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                        animation: 'fadeIn 0.5s ease-in-out',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>🎯 {filtroAplicadoMensaje}</span>
                        <div>
                            <button 
                                onClick={() => {
                                    clearAllFilters();
                                    setFiltroAplicadoMensaje("");
                                }}
                                style={{
                                    marginLeft: '15px',
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                Ver Todas
                            </button>
                            <button 
                                onClick={() => setFiltroAplicadoMensaje("")}
                                style={{
                                    marginLeft: '10px',
                                    background: 'none',
                                    border: 'none',
                                    color: '#2e7d32',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                {resultadoFiltrado && (
                    <section className="filtered-results" key={`results-${resultadoFiltrado.length}-${Date.now()}`}>
                        <h2 className="results-title">
                            Resultados de búsqueda: {resultadoFiltrado.length} {resultadoFiltrado.length === 1 ? 'propiedad encontrada' : 'propiedades encontradas'}
                            <small style={{fontSize: '12px', color: '#666', marginLeft: '10px'}}>
                                (Página {currentPage} de {getTotalPages(resultadoFiltrado.length)})
                            </small>
                        </h2>
                        <div style={{fontSize: '14px', color: '#666', marginBottom: '15px'}}>
                            Filtros activos: 
                            {filtroNombre && ` Nombre: "${filtroNombre}"`}
                            {filtroModalidad && ` Modalidad: "${filtroModalidad}"`}
                            {filtroTipo && ` Tipo: "${filtroTipo}"`}
                            {filtroUbicacion && ` Ubicación: "${filtroUbicacion}"`}
                            {!filtroNombre && !filtroModalidad && !filtroTipo && !filtroUbicacion && ' Ninguno'}
                        </div>
                        {resultadoFiltrado.length > 0 ? (
                            <>
                                <div className="properties-grid">
                                    {getPaginatedProperties(resultadoFiltrado).map((p, i) => (
                                        <PropertyCard
                                            key={`filtered-${p.id_propiedad || p.titulo || i}-${resultadoFiltrado.length}-${currentPage}`}
                                            title={p.titulo}
                                            price={p.precio}
                                            image={Array.isArray(p.fotos) ? p.fotos[0] : (typeof p.fotos === 'string' ? p.fotos.split(',')[0] : '') || ''}
                                            onClick={() => setSelectedProperty(p)}
                                            property={p}
                                        />
                                    ))}
                                </div>
                                
                                {/* Paginación */}
                                {getTotalPages(resultadoFiltrado.length) > 1 && (
                                    <div className="pagination-container">
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            ← Anterior
                                        </button>
                                        
                                        <div className="pagination-numbers">
                                            {Array.from({ length: getTotalPages(resultadoFiltrado.length) }, (_, i) => i + 1)
                                                .filter(page => {
                                                    // Mostrar primera página, última página, página actual y páginas adyacentes
                                                    return page === 1 || 
                                                           page === getTotalPages(resultadoFiltrado.length) || 
                                                           Math.abs(page - currentPage) <= 1;
                                                })
                                                .map((page, index, array) => (
                                                    <React.Fragment key={page}>
                                                        {index > 0 && array[index - 1] !== page - 1 && (
                                                            <span className="pagination-dots">...</span>
                                                        )}
                                                        <button
                                                            className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === getTotalPages(resultadoFiltrado.length)}
                                        >
                                            Siguiente →
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-results">
                                <p>No se encontraron propiedades que coincidan con los filtros seleccionados.</p>
                                <p>Intenta ajustar los criterios de búsqueda o limpiar algunos filtros.</p>
                                <button 
                                    onClick={clearAllFilters}
                                    style={{
                                        marginTop: '10px',
                                        padding: '8px 16px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Limpiar todos los filtros
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/*TABS: Destacadas/Remates */}
                <section className="featured-section">
                    {/* Botones tabs */}
                    <div className="tab-buttons">
                        <button
                            className={`tab-button ${activeTab === 'destacadas' ? 'active-destacadas' : ''}`}
                            onClick={() => handleTabChange('destacadas')}
                        >
                            Propiedades Destacadas ({properties.filter(p => p.destacado === true).length})
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'remates' ? 'active-remates' : ''}`}
                            onClick={() => handleTabChange('remates')}
                        >
                            Remates Inmobiliarios ({properties.filter(p => JSON.parse(p.caracteristicas || '{}').modalidad === 'remate').length})
                        </button>
                    </div>

                 

                    {/* Animación con transición de opacidad */}
                    <div className={`properties-fade ${!fade ? 'fade-out' : ''}`}>
                        {propiedadesFiltradas.length > 0 ? (
                            <>
                                <div className="properties-grid">
                                    {getPaginatedProperties(propiedadesFiltradas).map((p, i) => (
                                        <div key={`${activeTab}-${p.id_propiedad || i}-${currentPage}`} style={{ flex: '0 0 auto' }}>
                                            <PropertyCard
                                                title={p.titulo}
                                                price={p.precio}
                                                image={Array.isArray(p.fotos) ? p.fotos[0] : typeof p.fotos === 'string' ? p.fotos.split(',')[0] : ''}
                                                onClick={() => setSelectedProperty(p)}
                                                property={p}
                                            />
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Paginación para tabs */}
                                {getTotalPages(propiedadesFiltradas.length) > 1 && (
                                    <div className="pagination-container">
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            ← Anterior
                                        </button>
                                        
                                        <div className="pagination-numbers">
                                            {Array.from({ length: getTotalPages(propiedadesFiltradas.length) }, (_, i) => i + 1)
                                                .filter(page => {
                                                    // Mostrar primera página, última página, página actual y páginas adyacentes
                                                    return page === 1 || 
                                                           page === getTotalPages(propiedadesFiltradas.length) || 
                                                           Math.abs(page - currentPage) <= 1;
                                                })
                                                .map((page, index, array) => (
                                                    <React.Fragment key={page}>
                                                        {index > 0 && array[index - 1] !== page - 1 && (
                                                            <span className="pagination-dots">...</span>
                                                        )}
                                                        <button
                                                            className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === getTotalPages(propiedadesFiltradas.length)}
                                        >
                                            Siguiente →
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-properties-tab">
                                <p>No hay {activeTab === 'destacadas' ? 'propiedades destacadas' : 'remates inmobiliarios'} disponibles en este momento.</p>
                            </div>
                        )}
                    </div>
                </section>


                <section className="all-properties-section">
                    <h2 className="all-properties-title">
                        Todas Las Propiedades Disponibles: 
                        <span> 
                            {isLoading ? 'Cargando...' : 
                             properties.length > 0 ? 
                             `${properties.length} ${properties.length === 1 ? 'propiedad' : 'propiedades'}` :
                             'No hay propiedades disponibles'
                            } 
                        </span>
                      
                    </h2>
                    <div className="properties-grid">
                        {isLoading ? (
                            <div className="loading-container">
                                <p>Cargando propiedades...</p>
                            </div>
                        ) : properties.length > 0 ? (
                            properties.map((p, i) => (
                                <PropertyCard
                                    key={p.id_propiedad || i}
                                    title={p.titulo}
                                    price={p.precio}
                                    image={Array.isArray(p.fotos) && p.fotos.length > 0 ? p.fotos[0] : ''}
                                    onClick={() => setSelectedProperty(p)}
                                    property={p}
                                />
                            ))
                        ) : (
                            <div className="no-results">
                                <p>No se encontraron propiedades disponibles.</p>
                                <p>Verifica tu conexión a internet o intenta nuevamente más tarde.</p>
                            </div>
                        )}
                    </div>
                </section>

            </main>
            <div className="footer-wrapper">
                <Footer />
            </div>
            {selectedProperty && (
                <PropertyModal
                    property={selectedProperty}
                    onClose={() => setSelectedProperty(null)}
                />
            )}
        </Layout>
    );
}